import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { capitalizeFirstLetter } from 'helpers/General'
import {
    TitleAlignment,
    Field,
    RECORD_STREAMS_TYPES, 
    RecordSelected, 
    ExternalLink, 
    BrandContent, 
    WebsiteLinks, 
    WebsiteLink,
    bgValueType,
    AlbumTrack,
    SocialMediaType,
    StreamingService,
    WebsiteStyle,
    CurrentBrand,
    ContentMetadata
} from 'modules/Types'
import { THEME_STYLES, ThemeStyleKey } from 'pages/WebsitePage/components/CustomizeWebsite/components/StyleSection/ThemeSelectorSection/utils'
import { WebsiteDataType } from "modules/Types"
import { SymphonyLogoStyleKey } from 'pages/WebsitePage/components/SymphonyLogoEditorCard'
import { } from "modules/Types"
import { SocialMedia } from "modules/Types"
import { websiteLinkInitObj } from "modules/Types"
import { getBrandContent_api } from 'api/brandContentApi'
import { searchSpotifyReleases } from 'api/platformApi'
import useCurrentBrand, { BrandExistsState } from './useCurrentBrand'
import dayjs from 'dayjs'
import { DATE_OPTIONS } from 'pages/DashboardPage/utils'
import { getWebsiteData_api } from 'api/dashboardApi'
import { WebsiteType } from 'modules/Const'

const { RECORD_STREAMS, DATA_COLLECTOR, TOUR } = WebsiteType
const { BASIC } = THEME_STYLES

/** Props for initializing the website editor */
interface InitializeWebsiteEditorProps {
    /** New website slug when creating a website from scratch */
    newWebsiteSlug?: string;
    /** URL for importing content (e.g. Spotify URL) */
    url?: string | null;
    /** Existing website slug when editing */
    websiteSlug?: string;
    /** Type of website being created/edited */
    websiteType?: WebsiteType;
}

interface WebsiteState {
    subtitle?: string
    streamingLinksDictionary?: WebsiteLinks
    socialMediaIcons?: WebsiteLinks
    titleText?: string
    showSecondaryLine?: boolean
    customLinksAdded: WebsiteLink[]
    isNewLink: boolean
    customLinkOptions: WebsiteLink[]
    enableSocialIcons?: boolean
    removeSymphonyBrand?: boolean
    addAffiliateLinkToLogo: boolean
    selectedSymphonyLogoStyle: SymphonyLogoStyleKey
    fields?: Field[]
    submitButtonText?: string
    themeStyle?: ThemeStyleKey
    fullVideoUrl?: string
    styleType?: string
    fontThemeStyle?: string
    embedYt?: boolean
    youtubeVideoLink?: string
    mainColor?: string
    recordSelected?: RecordSelected
    secondaryColor?: string
    bgValue?: bgValueType
    bgType?: string
    youtubeID?: string
    isProject?: boolean
    showRecordSelector?: boolean
    selectedPreviewTrack: AlbumTrack | null
    websiteStats: WebsiteDataType | null
    customThumbnailUrl: string | null
    customThumbnailViewStyle: 'cover' | 'contain'
    tourExternalUrl?: string
    tourDates: any[]
    hidePastTourDates: boolean
    titleAlignment: TitleAlignment
    brandContent: BrandContent | null
    loadingContent: boolean
    websiteType: WebsiteType | null

    // Actions
    setCustomThumbnailUrl: (url: string | null) => void
    setCustomThumbnailViewStyle: (style: 'cover' | 'contain') => void
    setIsProject: (isProject: boolean | undefined) => void
    setShowRecordSelector: (show: boolean | undefined) => void
    setSelectedPreviewTrack: (track: AlbumTrack | null) => void
    setYoutubeEmbedId: (id: string | undefined) => void
    setThemeStyle: (style: ThemeStyleKey | undefined) => void
    setFullVideoUrl: (url: string | undefined) => void
    setStyleType: (type: string | undefined) => void
    setFontThemeStyle: (style: string | undefined) => void
    setEmbedYt: (embed: boolean | undefined) => void
    setYTVideoLink: (link: string | undefined) => void
    setMainColor: (color: string | undefined) => void
    setRecordSelected: (record: RecordSelected | undefined) => void
    setSecondaryColor: (color: string | undefined) => void
    setBgValue: (value: bgValueType | undefined) => void
    setBgType: (type: string | undefined) => void
    setSubmitButtonText: (text: string | undefined) => void
    setFields: (fields: Field[] | undefined) => void
    setRemoveSymphonyBrand: (remove: boolean | undefined) => void
    setAddAffiliateLinkToLogo: (add: boolean) => void
    setSelectedSymphonyLogoStyle: (style: SymphonyLogoStyleKey) => void
    setEnableSocialIcons: (enable: boolean | undefined) => void
    setCustomLinkOptions: (options: WebsiteLink[]) => void
    setIsNewLink: (isNew: boolean) => void
    setCustomLinksAdded: (links: WebsiteLink[]) => void
    setShowSecondaryLine: (show: boolean | undefined) => void
    setTitleText: (text: string | undefined) => void
    setStreamingLinksDictionary: (dict: WebsiteLinks | undefined) => void
    setSubtitle: (subtitle: string | undefined) => void
    setSocialMediaIcons: (icons: WebsiteLinks | undefined) => void
    setWebsiteStats: (websiteStats: WebsiteDataType | null) => void
    setTourExternalUrl: (url: string | undefined) => void
    setTourDates: (dates: any[]) => void
    setHidePastTourDates: (hide: boolean) => void
    setTitleAlignment: (alignment: TitleAlignment) => void
    setBrandContent: (content: BrandContent | null) => void

    /** Initializes the website editor with content and sets up all required state */
    initializeWebsiteEditor: (params: InitializeWebsiteEditorProps) => Promise<void>

    /** Fetches website analytics data for the specified time range */
    getWebsiteData: (currentTimeRange?: DATE_OPTIONS, customStartDate?: string, customEndDate?: string) => Promise<void>
}

const useWebsite = create<WebsiteState>()(
    immer((set, get) => ({
        // Initial state
        websiteType: null,
        subtitle: undefined,
        streamingLinksDictionary: undefined,
        socialMediaIcons: undefined,
        titleText: undefined,
        showSecondaryLine: undefined,
        customLinksAdded: [],
        isNewLink: false,
        customLinkOptions: [],
        enableSocialIcons: undefined,
        removeSymphonyBrand: undefined,
        addAffiliateLinkToLogo: false,
        selectedSymphonyLogoStyle: SymphonyLogoStyleKey.BANNER,
        fields: undefined,
        submitButtonText: undefined,
        themeStyle: undefined,
        fullVideoUrl: undefined,
        styleType: undefined,
        fontThemeStyle: undefined,
        embedYt: undefined,
        youtubeVideoLink: undefined,
        mainColor: undefined,
        recordSelected: undefined,
        secondaryColor: undefined,
        bgValue: undefined,
        bgType: undefined,
        youtubeID: undefined,
        isProject: undefined,
        showRecordSelector: undefined,
        selectedPreviewTrack: null,
        websiteStats: null,
        customThumbnailUrl: null,
        customThumbnailViewStyle: 'cover',
        tourExternalUrl: undefined,
        tourDates: [],
        hidePastTourDates: false,
        titleAlignment: 'center',
        brandContent: null,
        loadingContent: false,

        // Actions
        setCustomThumbnailUrl: (url) => set((state) => {
            state.customThumbnailUrl = url
        }),
        setCustomThumbnailViewStyle: (style) => set((state) => {
            state.customThumbnailViewStyle = style
        }),
        setIsProject: (isProject) => set((state) => {
            state.isProject = isProject
        }),
        setShowRecordSelector: (show) => set((state) => {
            state.showRecordSelector = show
        }),
        setSelectedPreviewTrack: (track) => set((state) => {
            state.selectedPreviewTrack = track
        }),
        setYoutubeEmbedId: (id) => set((state) => {
            state.youtubeID = id
        }),
        setThemeStyle: (style) => set((state) => {
            state.themeStyle = style
        }),
        setFullVideoUrl: (url) => set((state) => {
            state.fullVideoUrl = url
        }),
        setStyleType: (type) => set((state) => {
            state.styleType = type
        }),
        setFontThemeStyle: (style) => set((state) => {
            state.fontThemeStyle = style
        }),
        setEmbedYt: (embed) => set((state) => {
            state.embedYt = embed
        }),
        setYTVideoLink: (link) => set((state) => {
            state.youtubeVideoLink = link
        }),
        setMainColor: (color) => set((state) => {
            state.mainColor = color
        }),
        setRecordSelected: (record) => set((state) => {
            state.recordSelected = record
        }),
        setSecondaryColor: (color) => set((state) => {
            state.secondaryColor = color
        }),
        setBgValue: (value) => set((state) => {
            state.bgValue = value
        }),
        setBgType: (type) => set((state) => {
            state.bgType = type
        }),
        setSubmitButtonText: (text) => set((state) => {
            state.submitButtonText = text
        }),
        setFields: (fields) => set((state) => {
            state.fields = fields
        }),
        setRemoveSymphonyBrand: (remove) => set((state) => {
            state.removeSymphonyBrand = remove
        }),
        setAddAffiliateLinkToLogo: (add) => set((state) => {
            state.addAffiliateLinkToLogo = add
        }),
        setSelectedSymphonyLogoStyle: (style) => set((state) => {
            state.selectedSymphonyLogoStyle = style
        }),
        setEnableSocialIcons: (enable) => set((state) => {
            state.enableSocialIcons = enable
        }),
        setCustomLinkOptions: (options) => set((state) => {
            state.customLinkOptions = options
        }),
        setIsNewLink: (isNew) => set((state) => {
            state.isNewLink = isNew
        }),
        setCustomLinksAdded: (links) => set((state) => {
            state.customLinksAdded = links
        }),
        setShowSecondaryLine: (show) => set((state) => {
            state.showSecondaryLine = show
        }),
        setTitleText: (text) => set((state) => {
            state.titleText = text
        }),
        setStreamingLinksDictionary: (dict) => set((state) => {
            state.streamingLinksDictionary = dict
        }),
        setSubtitle: (subtitle) => set((state) => {
            state.subtitle = subtitle
        }),
        setSocialMediaIcons: (icons) => set((state) => {
            state.socialMediaIcons = icons
        }),
        setWebsiteStats: (websiteStats) => set((state) => {
            state.websiteStats = websiteStats
        }),
        setTourExternalUrl: (url) => set((state) => {
            state.tourExternalUrl = url
        }),
        setTourDates: (dates) => set((state) => {
            state.tourDates = dates
        }),
        setHidePastTourDates: (hide) => set((state) => {
            state.hidePastTourDates = hide
        }),
        setTitleAlignment: (alignment) => set((state) => {
            state.titleAlignment = alignment
        }),
        setBrandContent: (content) => set((state) => {
            state.brandContent = content
        }),

        // Initialize store
        initializeWebsiteEditor: async ({ newWebsiteSlug, url, websiteSlug, websiteType }: InitializeWebsiteEditorProps) => {
            const { currentBrand, isPaidBrand, isProBrand } = useCurrentBrand.getState() as BrandExistsState;

            set((state) => {
                // Reset all state to initial values first
                resetToInitialState(state)
                state.loadingContent = true
            })

            try {
                let content: BrandContent | null = await getInitialContent({ newWebsiteSlug, url, websiteSlug, currentBrand, websiteType });
                if (!content || !websiteType) return;

                const styles = getStylesFromContent(content);
                const preset = content.brand_preset;
                const backgroundType = styles.bgType || (content.content_metadata.thumbnail_url ? 'artwork' : 'color');

                set((state) => {
                    state.websiteType = websiteType;
                    setCommonState(state, {
                        content,
                        styles,
                        preset,
                        backgroundType,
                        isProBrand
                    });

                    if (websiteType === WebsiteType.RECORD_STREAMS) {
                        setRecordStreamsState(state, content!, styles, preset);
                    }
                    else if (websiteType === WebsiteType.DATA_COLLECTOR) {
                        setDataCollectorState(state, content!, styles);
                    }
                    else if (websiteType === WebsiteType.TOUR) {
                        setTourState(state, content!, styles);
                    }
                })

            } catch (error) {
                console.error('Error initializing website editor:', error);
            } finally {
                set((state) => {
                    state.loadingContent = false
                })
            }
        },

        getWebsiteData: async (currentTimeRange = 'last-30-days', customStartDate?: string, customEndDate?: string) => {
            try {
                const { currentBrand } = useCurrentBrand.getState() as BrandExistsState;
                const { brandContent } = get();

                let startDate = null;
                let endDate = null;

                switch (currentTimeRange) {
                    case 'last-30-days':
                        endDate = dayjs().format('YYYY-MM-DD');
                        startDate = dayjs().subtract(30, 'days').startOf('day').format('YYYY-MM-DD');
                        break;
                    case 'last-7-days':
                        endDate = dayjs().format('YYYY-MM-DD');
                        startDate = dayjs().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
                        break;
                    case 'last-3-months':
                        endDate = dayjs().format('YYYY-MM-DD');
                        startDate = dayjs().subtract(3, 'months').startOf('day').format('YYYY-MM-DD');
                        break;
                    case 'all-time':
                        endDate = dayjs().format('YYYY-MM-DD');
                        startDate = null;
                        break;
                    case 'custom':
                        if (customStartDate && customEndDate) {
                            startDate = customStartDate;
                            endDate = customEndDate;
                        } else {
                            throw new Error('Custom date range requires both start and end dates');
                        }
                        break;
                }

                const websiteData = await getWebsiteData_api({
                    brandSlug: currentBrand?.slug || '',
                    contentSlug: brandContent?.slug || '',
                    startDate,
                    endDate
                });

                set((state) => {
                    state.websiteStats = websiteData as WebsiteDataType
                });
            } catch (error) {
                console.error('Error fetching website data:', error);
            }
        },

        // handlePublishWebsite = async () => {
        //     setLoading(true)
        //     try {
        //         const contextData = {
        //             bgType,
        //             bgValue,
        //             customLinkOptions,
        //             customLinksAdded,
        //             embedYt,
        //             enableSocialIcons,
        //             fields,
        //             fontThemeStyle,
        //             isNewLink,
        //             isProject,
        //             mainColor,
        //             socialMediaIcons,
        //             recordSelected,
        //             removeSymphonyBrand,
        //             selectedSymphonyLogoStyle,
        //             addAffiliateLinkToLogo,
        //             secondaryColor,
        //             selectedPreviewTrack,
        //             showRecordSelector,
        //             submitButtonText,
        //             showSecondaryLine,
        //             streamingLinks,
        //             styleType,
        //             subtitle,
        //             themeStyle,
        //             titleText,
        //             youtubeID,
        //             youtubeVideoLink,
        //             previewUrl: customThumbnailUrl || currentBrand?.image,
        //             customThumbnailViewStyle,
        //             tourDates,
        //             hidePastTourDates,
        //             titleAlignment,
        //         };

        //         const dataFormatted = formatDataToPublish(contextData, brandContent, currentBrand)
        //         dataFormatted.url = `${currentBrand?.slug}-${slug}`;
        //         dataFormatted.slug = slug;

        //         let socialIcons = currentBrand?.website?.socialIcons || []
        //         if (enableSocialIcons && socialMediaIcons) {
        //             socialIcons = Object.entries(socialMediaIcons).map(([key, value]: [string, WebsiteLink]) => value as WebsiteLink)
        //         }

        //         const brandDataToUpdate = {
        //             buttonConfig: styleType,
        //             enableSocialIcons,
        //             removeSymphonyBrand,
        //             selectedSymphonyLogoStyle,
        //             addAffiliateLinkToLogo,
        //             socialIcons,
        //         }

        //         const publishedContentResponse = await publishBrandContent_api(dataFormatted)
        //         console.log('publishedContentResponse', publishedContentResponse)
        //         await saveBrandWebsite_api(currentBrand?.slug || '', brandDataToUpdate)
        //         console.log('brandDataToUpdate', brandDataToUpdate)
        //         refreshSiteData(publishedContentResponse.slug);
        //         onPublished && onPublished();
        //         onClose();
        //     } catch (error: any) {
        //         if (error?.message?.length) {
        //             setAlreadyInUse(true)
        //             console.log('error', error)
        //         } else if (error?.response?.data?.message?.length) {
        //             setAlreadyInUse(true)
        //         }
        //     }
        //     setLoading(false)
        //     track('Published "New Website"', {
        //         type: brandContent.type,
        //         template: themeStyle,
        //         font: fontThemeStyle,
        //         linkStyle: styleType,
        //     })
        // },

        
    }))
)

export const dataCollectorFieldsInit = [
    {
        id: 'email_number',
        label: "Email / Phone Number",
        required: true,
        priority: 0,
        reserved: false,
    },
    {
        id: 'name',
        label: "What's your name?",
        required: true,
        priority: 0,
        reserved: false,
    },
];

export const socialMediaIconsInit: WebsiteLinks = {
    facebook: {
        ...websiteLinkInitObj,
        ...{
            title: 'Facebook',
            key: 'facebook',
            url: '',
            placeholder: 'Facebook profile URL',
        },
    },
    instagram: {
        ...websiteLinkInitObj,
        ...{
            title: 'Instagram',
            key: 'instagram',
            url: '',
            placeholder: 'Instagram profile URL',
        },
    },
    tiktok: {
        ...websiteLinkInitObj,
        ...{
            title: 'TikTok',
            key: 'tiktok',
            url: '',
            placeholder: 'TikTok profile URL',
        },
    },
    twitter: {
        ...websiteLinkInitObj,
        ...{
            title: 'Twitter',
            key: 'twitter',
            url: '',
            placeholder: 'X profile URL',
        },
    },
    youtube: {
        ...websiteLinkInitObj,
        ...{
            title: 'YouTube',
            key: 'youtube',
            url: '',
            placeholder: 'YouTube profile URL',
        },
    },
};

export const streamingTitleDict: {
    [key in StreamingService]: string;
} = {
    spotify: 'Spotify',
    apple_music: 'Apple Music',
    amazon_music: 'Amazon Music',
    youtube: 'YouTube Music',
    deezer: 'Deezer',
    tidal: 'Tidal',
    soundcloud: 'SoundCloud',
    audiomack: 'AudioMack',
    yandex: 'Yandex',
    tiktok: 'TikTok',
};

export const socialMediaTitleDict: Record<SocialMediaType, string> = {
    [SocialMedia.FACEBOOK]: 'Facebook',
    [SocialMedia.INSTAGRAM]: 'Instagram',
    [SocialMedia.TIKTOK]: 'TikTok',
    [SocialMedia.TWITTER]: 'Twitter',
    [SocialMedia.YOUTUBE]: 'YouTube',
};

/** Helper to initialize social media icons */
const initializeSocialMediaIcons = (content: BrandContent): WebsiteLinks => {
    const socialIcons = content?.brand_preset?.socialIcons || [];
    if (!socialIcons.length) {
        return socialMediaIconsInit;
    }

    const newSocialIcons: WebsiteLinks = {};
    for (const socialIcon of socialIcons) {
        newSocialIcons[socialIcon.key] = {
            ...socialIcon,
            title: socialMediaTitleDict[socialIcon.key as SocialMediaType],
        };
    }
    return newSocialIcons;
};

/** Helper to initialize external links */
const initializeExternalLinks = (
    content: BrandContent,
    existingCustomLinks: WebsiteLink[] = []
): {
    streamingLinks: WebsiteLinks,
    customLinks: WebsiteLink[]
} => {
    let externalLinks: ExternalLink[] = content?.content_metadata?.external_links || [];
    const newStreamingLinks: WebsiteLinks = {};
    const customLinks = [...existingCustomLinks];

    // Add default Spotify link for playlists
    if (content?.content_metadata.type === RECORD_STREAMS_TYPES.PLAYLIST && !externalLinks.length) {
        externalLinks.push({
            name: 'spotify',
            url: content?.content_metadata.record_url || '',
            enabled: true,
            type: 'spotify'
        });
    }

    for (const link of externalLinks) {
        const newWebsiteLink = {
            ...websiteLinkInitObj,
            title: streamingTitleDict[link.name as StreamingService] || capitalizeFirstLetter(link.name),
            key: link.name,
            type: link.type,
            url: link.url,
            enabled: (
                content?.content_metadata &&
                'website' in content?.content_metadata &&
                'enabled' in link
            ) ? link.enabled : true,
        };

        if (link.type === 'custom' || link.type === 'custom_link') {
            customLinks.push(newWebsiteLink as WebsiteLink);
        }
        newStreamingLinks[link.name] = newWebsiteLink as WebsiteLink;
    }

    return { streamingLinks: newStreamingLinks, customLinks };
};

const createDefaultContent = (websiteType: WebsiteType, currentBrand: CurrentBrand): BrandContent => {
    const defaultContentMetadata: ContentMetadata = {
        name: '',
        spotify_id: '',
        spotify_uri: '',
        thumbnail_url: '',
        record_url: '',
        primary_artist: currentBrand.name,
        preview_url: '',
        type: 'single',
        updateCount: 0,
        external_links: [],
        spotify_release_type: '',
        external_ids: {},
        ...(websiteType === WebsiteType.DATA_COLLECTOR ? { tracks: [] } : {})
    };

    return {
        brand_id: currentBrand.id,
        name: websiteType === WebsiteType.DATA_COLLECTOR ? 'New data collector site' : 'New Events & Tours site',
        type: websiteType,
        content_metadata: defaultContentMetadata,
        id: 0,
        slug: '',
        url: '',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
    };
};

const createRecordInitObject = (content: BrandContent): RecordSelected => ({
    full_info: true,
    name: content.content_metadata.name || '',
    preview_url: content.content_metadata.preview_url || '',
    explicit: content.content_metadata.explicit ?? false,
    primary_artist: content.content_metadata.primary_artist || '',
    record_url: content.content_metadata.record_url || '',
    spotify_id: content.content_metadata.spotify_id || '',
    spotify_uri: content.content_metadata.spotify_uri || '',
    thumbnail_url: content.content_metadata.thumbnail_url || '',
    type: content.content_metadata.type || '',
    tracks: content.content_metadata.tracks || [],
    slug: content.slug || '',
    url: content.url || '',
    id: content.id || 0,
    total_tracks: content.content_metadata.type === 'playlist' ? 0 :
        (content.content_metadata.total_tracks || 0),
    spotify_release_type: content.content_metadata.spotify_release_type || '',
    release_date: content.content_metadata.type === 'playlist' ? '' :
        (content.content_metadata.release_date || ''),
    external_links: content.content_metadata.external_links || [{
        name: 'spotify',
        url: content.content_metadata.record_url || '',
        enabled: true,
        type: 'spotify'
    }],
});

/** Helper to get initial content based on params */
const getInitialContent = async ({
    newWebsiteSlug,
    url,
    websiteSlug,
    currentBrand,
    websiteType
}: {
    newWebsiteSlug?: string;
    url?: string | null;
    websiteSlug?: string;
    currentBrand: CurrentBrand;
    websiteType?: WebsiteType;
}): Promise<BrandContent | null> => {
    let content: BrandContent | null = null;

    if (newWebsiteSlug && currentBrand.slug) {
        const result = await getBrandContent_api(currentBrand.slug, newWebsiteSlug);
        content = result || null;
    } else if (url) {
        const result = await searchSpotifyReleases(url);
        content = result || null;
    } else if (websiteSlug && currentBrand.slug) {
        const result = await getBrandContent_api(currentBrand.slug, websiteSlug);
        content = result || null;
    }

    // Create default content if none exists
    if (!content && websiteType) {
        content = createDefaultContent(websiteType, currentBrand);
    }

    return content;
}

/** Helper to get styles from content metadata */
const getStylesFromContent = (content: BrandContent): WebsiteStyle => {
    return ('presave' in content.content_metadata && content.content_metadata.presave?.style)
        || ('website' in content.content_metadata && content.content_metadata.website?.style)
        || {};
}

/** Helper to reset state to initial values */
const resetToInitialState = (state: WebsiteState) => {
    // Reset common state
    state.websiteType = null
    state.subtitle = undefined
    state.streamingLinksDictionary = undefined
    state.socialMediaIcons = undefined
    state.titleText = undefined
    state.showSecondaryLine = undefined
    state.customLinksAdded = []
    state.isNewLink = false
    state.customLinkOptions = []
    state.enableSocialIcons = undefined
    state.removeSymphonyBrand = undefined
    state.addAffiliateLinkToLogo = false
    state.selectedSymphonyLogoStyle = SymphonyLogoStyleKey.BANNER
    state.fields = undefined
    state.submitButtonText = undefined
    state.themeStyle = undefined
    state.fullVideoUrl = undefined
    state.styleType = undefined
    state.fontThemeStyle = undefined
    state.embedYt = undefined
    state.youtubeVideoLink = undefined
    state.mainColor = undefined
    state.recordSelected = undefined
    state.secondaryColor = undefined
    state.bgValue = undefined
    state.bgType = undefined
    state.youtubeID = undefined
    state.isProject = undefined
    state.showRecordSelector = undefined
    state.selectedPreviewTrack = null
    state.websiteStats = null
    state.customThumbnailUrl = null
    state.customThumbnailViewStyle = 'cover'
    state.tourExternalUrl = undefined
    state.tourDates = []
    state.hidePastTourDates = false
    state.titleAlignment = 'center'
    state.brandContent = null
    state.loadingContent = false
}

/** Helper to set common state for all website types */
const setCommonState = (
    state: WebsiteState,
    { content, styles, preset, backgroundType, isProBrand }: {
        content?: BrandContent | null;
        styles: WebsiteStyle;
        preset: any;
        backgroundType: string;
        isProBrand: boolean;
    }
) => {
    state.brandContent = content ?? null
    state.customThumbnailUrl = styles.thumbnail_url || null
    state.customThumbnailViewStyle = 'cover'
    state.titleText = styles.titleText
    state.subtitle = styles.subtitle || ''
    state.showSecondaryLine = Boolean(styles.showSecondaryLine)
    state.bgType = backgroundType
    state.bgValue = {
        color: styles.bgColor || '#0B0113',
        artwork: backgroundType === 'artwork' ? 1 : 0,
    }
    state.mainColor = styles.primaryColor
    state.secondaryColor = styles.secondaryColor
    state.fontThemeStyle = styles.font || 'font1'
    state.removeSymphonyBrand = isProBrand ? (preset?.removeSymphonyBrand || false) : false
    state.addAffiliateLinkToLogo = isProBrand ? (preset?.addAffiliateLinkToLogo || isProBrand) : false
    state.selectedSymphonyLogoStyle = isProBrand ?
        (preset?.selectedSymphonyLogoStyle || SymphonyLogoStyleKey.BELOW) :
        SymphonyLogoStyleKey.BANNER
    state.titleAlignment = styles.titleAlignment || 'center'
    state.fullVideoUrl = styles.full_video_url
}

/** Helper to set Record Streams specific state */
const setRecordStreamsState = (state: WebsiteState, content: BrandContent, styles: WebsiteStyle, preset: any) => {
    const template = !styles.template && content.content_metadata.type === RECORD_STREAMS_TYPES.PLAYLIST
        ? THEME_STYLES.ART
        : (styles.template || THEME_STYLES.ART);

    const { streamingLinks, customLinks } = initializeExternalLinks(content, state.customLinksAdded);
    const socialMediaIcons = initializeSocialMediaIcons(content);
    const recordInitObj = createRecordInitObject(content);

    state.styleType = styles.buttonConfig || 'boxes'
    state.themeStyle = template
    state.recordSelected = recordInitObj
    state.isProject = Boolean(recordInitObj.tracks?.length)
    state.embedYt = Boolean(styles.youtubeEmbed?.url && styles.youtubeEmbed?.id)
    state.youtubeVideoLink = styles.youtubeEmbed?.url || ''
    state.youtubeID = styles.youtubeEmbed?.id || ''
    state.enableSocialIcons = preset?.enableSocialIcons ?? false
    state.streamingLinksDictionary = streamingLinks
    state.socialMediaIcons = socialMediaIcons
    state.customLinksAdded = customLinks
}

/** Helper to set Data Collector specific state */
const setDataCollectorState = (state: WebsiteState, content: BrandContent, styles: WebsiteStyle) => {
    state.submitButtonText = styles.submit_button_text || 'Subscribe'
    state.themeStyle = styles.template || BASIC
    state.fields = content.content_metadata.website?.fields || dataCollectorFieldsInit
}

/** Helper to set Tour specific state */
const setTourState = (state: WebsiteState, content: BrandContent, styles: WebsiteStyle) => {
    state.titleText = state.titleText || '2025 Tour'
    state.submitButtonText = styles.submit_button_text || 'Subscribe'
    state.themeStyle = styles.template || BASIC
    state.styleType = styles.buttonConfig || 'boxes'
    state.tourDates = content.content_metadata.website?.tourDates || []
    state.hidePastTourDates = content.content_metadata.website?.hidePastTourDates || false
}

export default useWebsite;